/**
 * Predicate registry — binds `PredicateRef` names in ruleset data to pure
 * implementations. A ruleset naming a predicate that is not registered is a
 * build-time data error; the engine throws rather than guessing.
 */
import type { AppliesPredicate, EvaluatePredicate } from "./predicate.js";
import { both_over_18, married_or_civil_partners } from "./age_and_marriage.js";
import { assessed_by_caseworker } from "./caseworker_judged.js";
import { cash_savings_sufficiency } from "./cash_savings_sufficiency.js";
import { cat_a_salaried_income } from "./cat_a_salaried_income.js";
import { cat_b_route } from "./cat_b_route.js";
import { cohabitation_two_years } from "./cohabitation.js";
import { combine_income_savings } from "./combine_income_savings.js";
import {
  english_at_level,
  english_exempt_by_nationality,
  english_exempt_other,
} from "./english_language.js";
import { evidence_specification_only } from "./evidence_format.js";
import {
  combination_route,
  employment_category_a,
  employment_category_b,
  savings_only_route,
  select_financial_category,
} from "./financial_category.js";
import { koll_interaction, life_in_uk_test } from "./koll.js";
import { select_mir_threshold } from "./select_mir_threshold.js";
import {
  accommodation_adequate,
  figures_are_gross,
  legal_right_to_occupy,
  marriage_legally_valid,
  met_in_person,
  not_within_prohibited_degrees,
  previous_relationships_ended,
  savings_lawful_and_controlled,
  without_public_funds,
} from "./simple.js";
import { sponsor_status } from "./sponsor_status.js";
import { whose_income_counts } from "./whose_income.js";

export const evaluatePredicates: Record<string, EvaluatePredicate> = {
  // financial
  select_mir_threshold,
  select_financial_category,
  cat_a_salaried_income,
  cat_b_route,
  cash_savings_sufficiency,
  combine_income_savings,
  whose_income_counts,
  figures_are_gross,
  savings_lawful_and_controlled,
  // sponsor — the first gate on the route
  sponsor_status,
  // relationship
  married_or_civil_partners,
  marriage_legally_valid,
  cohabitation_two_years,
  both_over_18,
  not_within_prohibited_degrees,
  met_in_person,
  previous_relationships_ended,
  // english
  english_at_level,
  english_exempt_by_nationality,
  english_exempt_other,
  // accommodation
  accommodation_adequate,
  legal_right_to_occupy,
  without_public_funds,
  // koll
  life_in_uk_test,
  koll_interaction,
  // evidence
  evidence_specification_only,
  // stated, never scored (ADR-0022)
  assessed_by_caseworker,
};

export const appliesPredicates: Record<string, AppliesPredicate> = {
  employment_category_a,
  employment_category_b,
  savings_only_route,
  combination_route,
};
